import { useEffect, useRef, useState } from 'react';
import { Crosshair, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { cargarGoogleMaps, CENTRO_POR_DEFECTO, type Lugar } from '@/lib/maps/googleMaps';
import { useT } from '@/lib/i18n/useT';

/**
 * Señalar en el mapa dónde es el evento.
 *
 * Existe porque muchos sitios no tienen portal: una explanada, un pabellón, la
 * entrada norte de un recinto. Escribir «Feria de Madrid» deja al equipo de
 * campo buscando la puerta correcta; un punto no.
 *
 * ## Se puede mover el marcador Y pulsar el mapa
 *
 * Las dos cosas hacen lo mismo a propósito. Arrastrar es lo natural cuando el
 * punto ya está cerca; pulsar, cuando está al otro lado de la ciudad y
 * arrastrarlo sería un viaje.
 *
 * ## La dirección se recalcula al soltar
 *
 * Cada movimiento se traduce a texto con geocodificación inversa, porque el
 * usuario guarda «una dirección» y no un par de números: si sólo cambiara el
 * punto, la ficha del evento seguiría enseñando la dirección vieja para una
 * ubicación nueva. Se hace **al soltar**, no mientras se arrastra — cada
 * llamada se cobra y en un arrastre hay cientos de posiciones.
 */
export function MapPickerDialog({
  value,
  onPick,
  onClose,
}: {
  value: Lugar;
  onPick: (v: Lugar) => void;
  onClose: () => void;
}) {
  const t = useT();
  const lienzo = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [buscando, setBuscando] = useState(false);
  const [elegido, setElegido] = useState<Lugar>(value);

  // El elegido en una ref: los manejadores del mapa se registran una sola vez y
  // sin esto verían siempre el valor del primer render.
  const actual = useRef(elegido);
  actual.current = elegido;

  useEffect(() => {
    let vivo = true;
    let mapa: google.maps.Map | null = null;
    let marcador: google.maps.Marker | null = null;
    let geocoder: google.maps.Geocoder | null = null;

    /** Punto → dirección. Si falla, se conserva el punto y no se pierde nada. */
    const traducir = (pos: google.maps.LatLng) => {
      if (!geocoder) return;
      setBuscando(true);
      geocoder.geocode({ location: pos }, (res, status) => {
        if (!vivo) return;
        setBuscando(false);
        const encontrado = status === 'OK' ? res?.[0] : null;
        setElegido({
          // Sin resultado se deja la dirección que ya había: un punto sin texto
          // es peor que un punto con el texto anterior, que al menos ubica.
          address: encontrado?.formatted_address ?? actual.current.address,
          latitude: pos.lat(),
          longitude: pos.lng(),
          placeId: encontrado?.place_id ?? null,
        });
      });
    };

    cargarGoogleMaps()
      .then(() => {
        if (!vivo || !lienzo.current) return;
        // Misma razón que en `AddressField`: aquí dentro una excepción se lleva
        // la pantalla, y un mapa que no carga sólo debe dejar el diálogo sin
        // mapa y con su motivo escrito.
        if (!window.google?.maps?.Map) {
          setCargando(false);
          setError('Google Maps no terminó de cargar sus bibliotecas.');
          return;
        }
        setCargando(false);

        const centro =
          value.latitude != null && value.longitude != null
            ? { lat: value.latitude, lng: value.longitude }
            : CENTRO_POR_DEFECTO;

        mapa = new google.maps.Map(lienzo.current, {
          center: centro,
          // Cerca si ya hay punto, lejos si se parte de cero: abrir a nivel de
          // calle en el centro por defecto haría creer que el evento es ahí.
          zoom: value.latitude != null ? 16 : 11,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
        });
        marcador = new google.maps.Marker({ map: mapa, position: centro, draggable: true });
        geocoder = new google.maps.Geocoder();

        marcador.addListener('dragend', () => {
          const p = marcador?.getPosition();
          if (p) traducir(p);
        });
        mapa.addListener('click', (e: google.maps.MapMouseEvent) => {
          if (!e.latLng) return;
          marcador?.setPosition(e.latLng);
          traducir(e.latLng);
        });
      })
      .catch((e: Error) => {
        if (!vivo) return;
        setCargando(false);
        setError(e.message);
      });

    return () => {
      vivo = false;
    };
    // Se monta una vez: el diálogo nace con el valor que le toca y a partir de
    // ahí manda lo que se elija dentro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hayPunto = elegido.latitude != null && elegido.longitude != null;

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{t('address.mapTitle')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
        {error ? (
          <p className="rounded-lg border border-[var(--color-xk-border)] p-4 text-xs text-[var(--color-xk-text-muted)]">
            {error}
          </p>
        ) : (
          <div className="relative h-[360px] w-full overflow-hidden rounded-lg border border-[var(--color-xk-border)]">
            <div ref={lienzo} className="h-full w-full" />
            {cargando ? (
              <div className="absolute inset-0 flex items-center justify-center bg-[var(--color-xk-bg-deep)]">
                <Loader2 className="h-5 w-5 animate-spin text-[var(--color-xk-text-muted)]" />
              </div>
            ) : null}
          </div>
        )}

        <p className="flex items-start gap-1.5 text-[11px] leading-snug text-[var(--color-xk-text-muted)]">
          <Crosshair className="mt-0.5 h-3 w-3 flex-none" />
          {t('address.mapHint')}
        </p>

        {/* Lo que se va a guardar, escrito. Confirmar sin ver el texto que
            produjo el punto es firmar a ciegas. */}
        <div className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2">
          <p className="text-xs text-[var(--color-xk-lavender)]">
            {buscando ? t('address.resolving') : elegido.address || t('address.nothingYet')}
          </p>
          {hayPunto ? (
            <p className="mt-0.5 font-mono text-[10px] text-[var(--color-xk-text-muted)]">
              {elegido.latitude!.toFixed(6)}, {elegido.longitude!.toFixed(6)}
            </p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
          >
            {t('stock.dialog.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onPick(elegido)}
            disabled={!hayPunto || buscando}
            className="rounded-lg bg-[var(--color-xk-accent)] px-5 py-2 text-xs font-extrabold text-[var(--color-fg-on-accent)] hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('address.useThis')}
          </button>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
